import styled from '@emotion/styled'
import * as React from 'react'

import { getColorValue } from '~theme/utils'
import { TButtonVariantProps } from '~ui/components/buttons/Button/buildButtonVariantProps'
import { Button, BUTTON_BORDER_WIDTH } from '~ui/components/buttons/Button/Button'
import { cn } from '~ui/libs/classnames'
import { durations } from '~ui/styleGuide/styles'
import { useTheme } from '~ui/theme/ThemeProvider'

type TButtonProps = Pick<
  PropsOf<typeof Button>,
  'width' | 'radius' | 'padding' | 'shadow' | 'shadowInset' | 'children' | 'maxWidth' | 'onClick'
>

type TStyledProps = {
  arrowColor: string
}

type TProps = TButtonProps &
  TButtonVariantProps &
  TestingProps & {
    /**
     * whether or not the arrow/triangle element should be rendered (typically only true for selected item)
     */
    withArrow?: boolean
    /**
     * direction of the whole RollingOptions, used also for the position of the arrow
     * used as css classes bellow
     */
    direction: 'row' | 'column'
  }

export const ROLLING_OPTIONS_ARROW_HEIGHT = `7px`
export const VERTICAL_ALIGN_OFFSET = `calc((${ROLLING_OPTIONS_ARROW_HEIGHT} - ${BUTTON_BORDER_WIDTH}) / 2 * -1)`

// get rid of the React error when passing unknown prop to the HTML element
const propForwardOptions = {
  shouldForwardProp: (propName: string) => propName !== `arrowColor`,
}

const StyledRollingOptionsButton = styled(Button, propForwardOptions)<TStyledProps>`
  --arrow-color: ${({ arrowColor }) => arrowColor};
  --arrow-height: ${ROLLING_OPTIONS_ARROW_HEIGHT};

  position: relative;

  &,
  &::after {
    transition:
      opacity ${durations.short}ms,
      background-color ${durations.short}ms,
      border-color ${durations.short}ms;
  }

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;

    border-style: solid;
    border-width: var(--arrow-height);
    border-color: transparent;
    opacity: 0;
  }

  &.row {
    &::after {
      bottom: calc(-1 * (var(--arrow-height)));
      border-bottom-width: 0px;
      border-top-color: var(--arrow-color);
    }
  }

  &.column {
    &::after {
      right: calc(-1 * (var(--arrow-height)));
      border-right-width: 0px;
      border-left-color: var(--arrow-color);
    }
  }

  &.with-arrow::after {
    opacity: 1;
  }
`

export const RollingOptionsButton: React.FC<TProps> = ({
  children,
  direction,
  withArrow,
  dataTestId,
  ...buttonProps
}) => {
  const theme = useTheme()
  const arrowColor = buttonProps.bgColor
    ? getColorValue(theme)(`activeColors.${buttonProps.bgColor}.normal`)
    : `transparent`

  return (
    <StyledRollingOptionsButton
      {...buttonProps}
      dataTestId={dataTestId}
      arrowColor={arrowColor}
      className={cn(direction, { 'with-arrow': withArrow })}
    >
      {children}
    </StyledRollingOptionsButton>
  )
}
